/**
 * Utilities for parsing the HTTP Retry-After header.
 *
 * RFC 7231 §7.1.3 allows two forms:
 *   1. A non-negative integer number of seconds (e.g. "120").
 *   2. An HTTP-date (e.g. "Wed, 21 Oct 2015 07:28:00 GMT").
 *
 * DRF's throttling always emits the seconds form, but we handle both so the
 * client stays correct if the API ever sits behind a proxy that rewrites it.
 */

const MAX_RETRY_AFTER_SECONDS = 60 * 60; // Cap at 1 hour to avoid pathological waits.

export const parseRetryAfter = (
  rawHeader: string | number | string[] | undefined | null
): number | null => {
  if (rawHeader === undefined || rawHeader === null) {
    return null;
  }

  const value = Array.isArray(rawHeader) ? rawHeader[0] : rawHeader;
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const asString = String(value).trim();

  // Try integer-seconds form first.
  if (/^\d+$/.test(asString)) {
    const seconds = parseInt(asString, 10);
    if (Number.isFinite(seconds) && seconds >= 0) {
      return Math.min(seconds, MAX_RETRY_AFTER_SECONDS);
    }
    return null;
  }

  // Only attempt HTTP-date parsing when the value looks date-shaped (contains
  // at least one alphabetic month/day token). This avoids bogus parses of
  // inputs like "-5" or "1.5" that some JS engines happily interpret.
  if (!/[A-Za-z]/.test(asString)) {
    return null;
  }

  const timestamp = Date.parse(asString);
  if (!Number.isNaN(timestamp)) {
    const deltaSeconds = Math.ceil((timestamp - Date.now()) / 1000);
    if (deltaSeconds <= 0) return 0;
    return Math.min(deltaSeconds, MAX_RETRY_AFTER_SECONDS);
  }

  return null;
};

/**
 * Convert Retry-After to milliseconds, with a fallback when the header is
 * missing or unparseable. Used by retry wrappers that need a delay value.
 */
export const getRetryAfterMs = (
  rawHeader: string | number | string[] | undefined | null,
  fallbackMs: number
): number => {
  const seconds = parseRetryAfter(rawHeader);
  if (seconds === null) return fallbackMs;
  return seconds * 1000;
};
